import { API_BASE } from "../config/api";
import { getCached, setCached, isCacheable } from "./apiCache";
import { mapErrorToUserMessage } from "./authErrors";

/** Build full URL: always use API_BASE for relative paths, no double slashes. */
function buildUrl(path) {
  if (path.startsWith("http")) return path;
  const base = (API_BASE || "").replace(/\/$/, "");
  const pathPart = path.startsWith("/") ? path : `/${path}`;
  return `${base}${pathPart}`;
}

export async function apiFetch(path, options = {}) {
  const { body, headers, ...restOptions } = options;
  const method = (options.method || "GET").toUpperCase();

  // Stale-while-revalidate: return cached data for GET if valid (no bypass)
  if (method === "GET" && !options.bypassCache && isCacheable(path, method)) {
    const cached = getCached(path, method);
    if (cached !== null) return cached;
  }

  // Save/Create property must send JSON only — never FormData.
  if (
    (path.includes("/api/properties") && (method === "PATCH" || method === "POST")) &&
    typeof FormData !== "undefined" &&
    body instanceof FormData
  ) {
    console.error("[apiFetch] Save/Create property must send JSON, not FormData.");
    throw new Error("Save/Create property must send JSON, not FormData.");
  }

  // Auto-attach idempotency key for write operations
  const mergedHeaders = { ...(headers || {}) };
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    if (!mergedHeaders["Idempotency-Key"]) {
      mergedHeaders["Idempotency-Key"] =
        typeof crypto !== "undefined" && crypto?.randomUUID
          ? crypto.randomUUID()
          : Date.now().toString() + Math.random().toString(36);
    }
  }

  // JSON body: stringify objects; never send FormData for JSON endpoints.
  const bodyString =
    body === undefined || body === null
      ? undefined
      : typeof body === "string"
        ? body
        : typeof body === "object" && !(typeof FormData !== "undefined" && body instanceof FormData)
          ? JSON.stringify(body)
          : body;

  const url = buildUrl(path);
  const res = await fetch(url, {
    method: options.method || "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...mergedHeaders,
    },
    ...(bodyString !== undefined ? { body: bodyString } : {}),
    ...restOptions,
  });

  // 🔒 No content
  if (res.status === 204) return null;

  // 🔒 401 on /api/auth/me = not authenticated (guest state); return null so caller can set user to null
  if (res.status === 401 && path.includes("/api/auth/me")) {
    return null;
  }

  // 🔒 404 on /api/balance/me = no wallet yet; return { balance: 0 } so UI shows 0 instead of throwing
  if (res.status === 404 && path.includes("/api/balance/me")) {
    return { balance: 0 };
  }

  // Handle error responses: throw { status, data } (backend sends { errorCode, message, supportId })
  if (!res.ok) {
    const text = await res.text();
    let data = { message: text || "Request failed" };
    try {
      const parsed = JSON.parse(text);
      data = {
        errorCode: parsed.errorCode || parsed.code,
        code: parsed.errorCode || parsed.code,
        message: parsed.message || parsed.error || data.message,
        supportId: parsed.supportId,
        fieldErrors: parsed.fieldErrors,
      };
    } catch (_) {}
    throw { status: res.status, data };
  }

  const data = await res.json();
  if (method === "GET" && isCacheable(path, method)) setCached(path, data, method);
  return data;
}

export default apiFetch;
export { API_BASE as BACKEND_URL };

// Minimal compatibility stubs (no auth)
export const getUserOrders = async () => {
  try {
    const [openRes, p2pRes, txRes] = await Promise.all([
      apiFetch("/api/orders/open").catch(() => null),
      apiFetch("/api/p2p/open-orders").catch(() => null),
      apiFetch("/api/transactions?limit=50").catch(() => null),
    ]);
    const open = Array.isArray(openRes) ? openRes : (openRes?.orders || []);
    const p2p = Array.isArray(p2pRes) ? p2pRes : (p2pRes?.orders || p2pRes?.data || []);
    const tx = Array.isArray(txRes) ? txRes : (txRes?.transactions || txRes?.data || []);
    // Normalize p2p orders
    const p2pNorm = p2p.map(o => ({ ...o, source: 'p2p', status: o.status || 'open', type: 'SELL_ORDER' }));
    // Normalize transactions
    const txNorm = tx.map(t => ({ ...t, source: 'transaction', orderId: t._id }));
    return [...open, ...p2pNorm, ...txNorm];
  } catch(e) {
    return [];
  }
};
export const getUserDistributions = () => apiFetch("/api/user/distributions");
export const cancelOrder = (id) => apiFetch(`/api/trading/cancel/${id}`, { method: "POST" });
export const getPaymentDetails = () => apiFetch("/api/user/payment-details");
export const savePaymentDetails = (body) => apiFetch("/api/user/payment-details", { method: "POST", body });
export const getProperty = (id) => apiFetch(`/api/properties/${id}`);
export const getRecentTrades = () => apiFetch("/api/trading/trades");
export const getOrderBook = (propertyId) => apiFetch(`/api/market/${propertyId}`);
export const createOrder = (body) => apiFetch("/api/trading/order", { method: "POST", body });
export const instantSellBack = (body) => apiFetch("/api/trading/instant-sell", { method: "POST", body });
export const signup = (payload) => apiFetch("/api/auth/signup", { method: "POST", body: payload });
/** Prefer auth-friendly message; works with apiFetch and axios errors. */
export const getErrorMessage = (err, fallback = "Something went wrong. Please try again.") => {
  if (!err) return fallback;
  const mapped = mapErrorToUserMessage(err);
  return mapped !== fallback ? mapped : (err.message || fallback);
};
export const API = API_BASE;
